"use client";

import type { Command } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import type { ReactNode } from "react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  LuBold,
  LuCode,
  LuEllipsis,
  LuImage,
  LuItalic,
  LuLink,
  LuQuote,
  LuStrikethrough,
  LuTable2,
  LuX,
} from "react-icons/lu";
import {
  MdHorizontalRule,
  MdOutlineFormatListBulleted,
  MdOutlineFormatListNumbered,
} from "react-icons/md";

import { DIALOG } from "@/components/app/dialogs/dialog-provider";
import { useLocalEntryDialog } from "@/components/app/dialogs/use-local-entry-dialog";
import {
  DialogDrawer,
  DialogDrawerClose,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerFooter,
  DialogDrawerHeader,
  DialogDrawerTitle,
} from "@/components/shared/DialogDrawer";
import {
  HorizontalItem,
  HorizontalItems,
  HorizontalItemsPopover,
  HorizontalItemsTrigger,
} from "@/components/shared/horizontal-items";
import { InfoTooltip } from "@/components/shared/info-tooltip";
import InputUpload from "@/components/shared/input-upload";
import { Alert } from "@/components/ui/alert";
import type { ButtonProps } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { cn } from "@/utils/lib";

import {
  editorCommands,
  editorSourceCommands,
  insertInlineImage,
  insertSourceImage,
  insertSourceTable,
  insertTable,
  runEditorCommand,
} from "./commands";
import { EDITOR_MODE } from "./constants";
import { useEditorFieldContext } from "./editor-context";
import { EDITOR_V3_OPEN_LINK_OPTIONS_EVENT } from "./editor-events";
import {
  applyEditorLink,
  type EditorLinkTarget,
  getEditorLinkDialogState,
  removeEditorLink,
} from "./link-utils";
import {
  emptyToolbarState,
  equalToolbarStates,
  getEditorToolbarState,
} from "./toolbar-state";
import { uploadInlineImageFile } from "./upload/inline-image-upload";

type IconButtonProps = ButtonProps & {
  active?: boolean;
  activationEvent?: "click" | "pointerdown";
  icon: ReactNode;
  onClick?: () => void;
  tooltip: string;
};

const SIZE_SELECTOR_WIDTH = 73;
const ICON_BUTTON_WIDTH = 36;

const IconButton = ({
  active = false,
  activationEvent = "pointerdown",
  icon,
  onClick,
  tooltip,
  children,
  ...props
}: IconButtonProps) => {
  const handledPointerActivationRef = useRef(false);

  const handleActivate = () => {
    onClick?.();
  };

  return (
    <InfoTooltip
      text={tooltip}
      mobile={false}
      className="
        hidden
        md:block
      "
    >
      <Button
        {...props}
        aria-label={props["aria-label"] ?? tooltip}
        size="icon"
        variant="ghost"
        className={cn(
          `
            flex size-9 shrink-0 cursor-pointer items-center justify-center
            rounded-sm px-2
            hover:text-muted-foreground
          `,
          active &&
            `
              border border-primary/60 bg-primary/10 text-primary
              hover:text-primary
            `,
          props.className,
        )}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse" && event.button !== 0) {
            return;
          }

          event.preventDefault();

          if (activationEvent === "pointerdown" && onClick) {
            handledPointerActivationRef.current = true;
            handleActivate();
          }
        }}
        onPointerUp={() => {
          window.setTimeout(() => {
            handledPointerActivationRef.current = false;
          }, 0);
        }}
        onClick={(event) => {
          if (handledPointerActivationRef.current) {
            handledPointerActivationRef.current = false;
            return;
          }

          if (!onClick) {
            return;
          }

          event.preventDefault();
          handleActivate();
        }}
      >
        {icon}
        {children}
      </Button>
    </InfoTooltip>
  );
};

export const EditorToolbar = () => {
  const { user } = useContext(UserContext);
  const { toast } = useToast();
  const { editorContext, getEditorInstance } = useEditorFieldContext<
    "body" | "gatedBody"
  >();
  const isSourceMode = editorContext.mode === EDITOR_MODE.SIMPLE;
  const { open: insertLinkDialogOpen, setOpen: setInsertLinkDialogOpen } =
    useLocalEntryDialog(DIALOG.EditorInsertLink);
  const { open: insertImageDialogOpen, setOpen: setInsertImageDialogOpen } =
    useLocalEntryDialog(DIALOG.EditorInsertImage);

  const [insertLinkState, setInsertLinkState] = useState({
    editing: false,
    target: null as EditorLinkTarget | null,
    text: "",
    title: "",
    url: "",
  });
  const [insertImageState, setInsertImageState] = useState({
    uploading: false,
    url: "",
  });
  const [toolbarState, setToolbarState] = useState(emptyToolbarState);
  const deferredEntryDialogOpenTimeoutRef = useRef<number | null>(null);

  const getView = useCallback(() => {
    try {
      return getEditorInstance("prosemirror");
    } catch {
      return null;
    }
  }, [getEditorInstance]);

  const refreshToolbarState = useCallback(
    (view: EditorView | null = getView()) => {
      if (!view || isSourceMode) {
        setToolbarState((current) =>
          equalToolbarStates(current, emptyToolbarState)
            ? current
            : emptyToolbarState,
        );
        return;
      }

      setToolbarState((current) => {
        const next = getEditorToolbarState(view.state);
        return equalToolbarStates(current, next) ? current : next;
      });
    },
    [getView, isSourceMode],
  );

  useEffect(() => {
    const view = getView();

    refreshToolbarState(view);

    if (!view) {
      return;
    }

    const refreshIfCurrentEditor = () => {
      if (!view.hasFocus() && !view.dom.contains(document.activeElement)) {
        return;
      }

      window.setTimeout(() => refreshToolbarState(view), 0);
    };
    const events: Array<keyof HTMLElementEventMap> = [
      "focus",
      "input",
      "keyup",
      "mouseup",
      "pointerup",
    ];

    events.forEach((eventName) => {
      view.dom.addEventListener(eventName, refreshIfCurrentEditor);
    });
    document.addEventListener("selectionchange", refreshIfCurrentEditor);

    return () => {
      events.forEach((eventName) => {
        view.dom.removeEventListener(eventName, refreshIfCurrentEditor);
      });
      document.removeEventListener("selectionchange", refreshIfCurrentEditor);
    };
  }, [getView, refreshToolbarState]);

  const run = (command: Command) => {
    const view = getView();

    if (!view) {
      return false;
    }

    const result = runEditorCommand(view, command);
    refreshToolbarState(view);
    return result;
  };

  const runModeCommand = (richCommand: Command, sourceCommand: Command) => {
    return run(isSourceMode ? sourceCommand : richCommand);
  };

  const resetInsertLink = useCallback(() => {
    setInsertLinkState({
      editing: false,
      target: null,
      text: "",
      title: "",
      url: "",
    });
  }, []);

  const resetInsertImage = () => {
    setInsertImageState({
      uploading: false,
      url: "",
    });
  };

  useEffect(
    () => () => {
      if (deferredEntryDialogOpenTimeoutRef.current !== null) {
        window.clearTimeout(deferredEntryDialogOpenTimeoutRef.current);
      }
    },
    [],
  );

  const deferEntryDialogOpen = useCallback((openDialog: () => void) => {
    if (deferredEntryDialogOpenTimeoutRef.current !== null) {
      window.clearTimeout(deferredEntryDialogOpenTimeoutRef.current);
    }

    deferredEntryDialogOpenTimeoutRef.current = window.setTimeout(() => {
      deferredEntryDialogOpenTimeoutRef.current = null;
      openDialog();
    }, 0);
  }, []);

  const openInsertLinkDialog = useCallback(
    ({ defer = false }: { defer?: boolean } = {}) => {
      const openDialog = () => {
        const view = getView();

        if (view) {
          const state = getEditorLinkDialogState(view, editorContext.mode);

          setInsertLinkState({
            editing: state.editing,
            target: state.target,
            text: state.text,
            title: state.title,
            url: state.href,
          });
        } else {
          resetInsertLink();
        }

        setInsertLinkDialogOpen(true);
      };

      if (defer) {
        deferEntryDialogOpen(openDialog);
        return;
      }

      openDialog();
    },
    [
      deferEntryDialogOpen,
      editorContext.mode,
      getView,
      resetInsertLink,
      setInsertLinkDialogOpen,
    ],
  );

  useEffect(() => {
    const view = getView();

    if (!view) {
      return;
    }

    const openFromEditorLink = () => {
      openInsertLinkDialog();
    };

    view.dom.addEventListener(
      EDITOR_V3_OPEN_LINK_OPTIONS_EVENT,
      openFromEditorLink,
    );

    return () => {
      view.dom.removeEventListener(
        EDITOR_V3_OPEN_LINK_OPTIONS_EVENT,
        openFromEditorLink,
      );
    };
  }, [getView, openInsertLinkDialog]);

  const insertCurrentLink = () => {
    const view = getView();

    if (!view || !insertLinkState.target) {
      return;
    }

    if (
      applyEditorLink(view, insertLinkState.target, {
        href: insertLinkState.url,
        text: insertLinkState.text,
        title: insertLinkState.title,
      })
    ) {
      refreshToolbarState(view);
      resetInsertLink();
      setInsertLinkDialogOpen(false);
    }
  };

  const removeCurrentLink = () => {
    const view = getView();

    if (!view || !insertLinkState.target) {
      return;
    }

    if (removeEditorLink(view, insertLinkState.target)) {
      refreshToolbarState(view);
      resetInsertLink();
      setInsertLinkDialogOpen(false);
    }
  };

  const insertImageUrl = () => {
    const src = insertImageState.url.trim();

    if (!src) {
      return;
    }

    if (
      run(
        isSourceMode
          ? insertSourceImage({
              alt: "Image",
              src,
              title: "Image",
            })
          : insertInlineImage({
              alt: "Image",
              src,
              title: "Image",
            }),
      )
    ) {
      resetInsertImage();
      setInsertImageDialogOpen(false);
    }
  };

  const uploadInlineImages = async (view: EditorView, files: File[]) => {
    setInsertImageState((prev) => ({
      ...prev,
      uploading: true,
    }));

    try {
      const urls = await Promise.all(files.map(uploadInlineImageFile));
      let inserted = 0;

      urls.forEach((url, index) => {
        if (!url) {
          return;
        }

        if (
          runEditorCommand(
            view,
            isSourceMode
              ? insertSourceImage({
                  alt: files[index].name,
                  src: url,
                  title: files[index].name,
                })
              : insertInlineImage({
                  alt: files[index].name,
                  src: url,
                  title: files[index].name,
                }),
          )
        ) {
          inserted += 1;
        }
      });

      if (inserted === 0) {
        throw new Error("No image was uploaded.");
      }

      resetInsertImage();
      setInsertImageDialogOpen(false);
    } catch (error) {
      setInsertImageState((prev) => ({
        ...prev,
        uploading: false,
      }));

      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload image",
        details: createToastErrorDetails(error),
      });
    }
  };

  const toolbarItems = (
    <>
      <HorizontalItem size={SIZE_SELECTOR_WIDTH}>
        <Select
          value={
            toolbarState.headingLevel
              ? String(toolbarState.headingLevel)
              : "size"
          }
          onValueChange={(value) => {
            if (value === "body") {
              runModeCommand(
                editorCommands.paragraph,
                editorSourceCommands.paragraph,
              );
              return;
            }

            runModeCommand(
              editorCommands.heading(+value),
              editorSourceCommands.heading(+value),
            );
          }}
        >
          <SelectTrigger className="w-auto text-xs">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent className="text-xs">
            <SelectItem className="hidden" value="size">
              Size&nbsp;&nbsp;
            </SelectItem>
            <SelectItem value="body">Paragraph</SelectItem>
            <SelectItem value="1" className="text-2xl">
              Heading 1
            </SelectItem>
            <SelectItem value="2" className="text-xl">
              Heading 2
            </SelectItem>
            <SelectItem value="3" className="text-lg">
              Heading 3
            </SelectItem>
            <SelectItem value="4" className="text-base">
              Heading 4
            </SelectItem>
            <SelectItem value="5" className="text-sm">
              Heading 5
            </SelectItem>
            <SelectItem value="6" className="text-xs">
              Heading 6
            </SelectItem>
          </SelectContent>
        </Select>
      </HorizontalItem>

      <HorizontalItem>
        <IconButton
          icon={<LuBold />}
          active={toolbarState.strong}
          tooltip="Bold"
          onClick={() =>
            runModeCommand(editorCommands.strong, editorSourceCommands.strong)
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<LuItalic />}
          active={toolbarState.em}
          tooltip="Italic"
          onClick={() =>
            runModeCommand(editorCommands.em, editorSourceCommands.em)
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<LuStrikethrough />}
          active={toolbarState.strike}
          tooltip="Strikethrough"
          onClick={() =>
            runModeCommand(editorCommands.strike, editorSourceCommands.strike)
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          activationEvent="click"
          icon={<LuLink />}
          active={toolbarState.link}
          tooltip="Insert Link"
          onClick={() => openInsertLinkDialog({ defer: true })}
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<MdOutlineFormatListBulleted />}
          active={toolbarState.bulletList}
          tooltip="Bullet List"
          onClick={() =>
            runModeCommand(
              editorCommands.bulletList,
              editorSourceCommands.bulletList,
            )
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<MdOutlineFormatListNumbered />}
          active={toolbarState.orderedList}
          tooltip="Numbered List"
          onClick={() =>
            runModeCommand(
              editorCommands.orderedList,
              editorSourceCommands.orderedList,
            )
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<LuQuote />}
          active={toolbarState.blockquote}
          tooltip="Quote"
          onClick={() =>
            runModeCommand(
              editorCommands.blockquote,
              editorSourceCommands.blockquote,
            )
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<LuCode />}
          active={toolbarState.codeBlock || toolbarState.code}
          tooltip="Code Block"
          onClick={() =>
            runModeCommand(
              editorCommands.codeBlock,
              editorSourceCommands.codeBlock,
            )
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<MdHorizontalRule />}
          tooltip="Divider"
          onClick={() =>
            runModeCommand(
              editorCommands.horizontalRule,
              editorSourceCommands.horizontalRule,
            )
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          icon={<LuTable2 />}
          active={toolbarState.table}
          tooltip="Table"
          onClick={() =>
            run(isSourceMode ? insertSourceTable() : insertTable())
          }
        />
      </HorizontalItem>
      <HorizontalItem>
        <IconButton
          activationEvent="click"
          icon={<LuImage />}
          tooltip="Inline Image"
          onClick={() =>
            deferEntryDialogOpen(() => setInsertImageDialogOpen(true))
          }
        />
      </HorizontalItem>
    </>
  );

  return (
    <div
      className="
        mx-auto flex justify-between gap-2 text-muted
        lg:flex-row lg:items-center
      "
    >
      <div className="flex grow items-center pb-4">
        <HorizontalItems
          defaultSize={ICON_BUTTON_WIDTH}
          triggerSize={ICON_BUTTON_WIDTH}
          containerProps={{
            className: "flex items-center",
          }}
        >
          {toolbarItems}

          <HorizontalItemsTrigger>
            <div className="group relative">
              <IconButton
                className="group"
                icon={
                  <>
                    <LuX
                      className="
                        hidden
                        group-data-[open=true]:block
                      "
                    />
                    <LuEllipsis
                      className="
                        hidden
                        group-data-[closed=true]:block
                      "
                    />
                  </>
                }
                tooltip="More"
              />
              <HorizontalItemsPopover
                className="my-2 -mr-2 flex flex-col gap-2 bg-card p-2"
                defaultSize={ICON_BUTTON_WIDTH}
              >
                {toolbarItems}
              </HorizontalItemsPopover>
            </div>
          </HorizontalItemsTrigger>
        </HorizontalItems>

        <DialogDrawer
          nested
          open={insertImageDialogOpen || insertImageState.uploading}
          onOpenChange={(open) => {
            if (open) {
              setInsertImageDialogOpen(true);
              return;
            }

            if (insertImageState.uploading) {
              return false;
            }

            resetInsertImage();
            setInsertImageDialogOpen(false);
          }}
        >
          <DialogDrawerContent
            className="
              gap-0
              sm:max-w-[425px]
            "
          >
            <DialogDrawerHeader>
              <DialogDrawerTitle>Inline Image</DialogDrawerTitle>
              <DialogDrawerDescription className="hidden">
                Insert an inline image.
              </DialogDrawerDescription>
            </DialogDrawerHeader>
            <div className="p-6">
              <div className="flex flex-col gap-2">
                {user && (
                  <InputUpload
                    title="Upload Inline Images"
                    disabled={insertImageState.uploading}
                    accept="image/*"
                    multiple
                    onChange={async (event) => {
                      const files = Array.from(event.currentTarget.files ?? []);
                      event.currentTarget.value = "";

                      if (files.length === 0) {
                        return;
                      }

                      const view = getView();

                      if (!view) {
                        return;
                      }

                      await uploadInlineImages(view, files);
                    }}
                  />
                )}
                {!user && <Alert>Please sign in to upload images</Alert>}
              </div>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div
                  className="
                    relative flex justify-center text-[10px] tracking-[1px]
                    uppercase
                  "
                >
                  <span className="bg-background px-4 text-muted">
                    or add inline image URL
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <Label htmlFor="editor-v3-image-url">Inline image URL</Label>
                <Input
                  id="editor-v3-image-url"
                  placeholder="https://..."
                  disabled={insertImageState.uploading}
                  value={insertImageState.url}
                  onChange={(event) => {
                    setInsertImageState((prev) => ({
                      ...prev,
                      url: event.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            <DialogDrawerFooter className="flex flex-row items-center gap-2">
              <Button
                loading={insertImageState.uploading}
                disabled={
                  insertImageState.uploading || !insertImageState.url.trim()
                }
                onClick={insertImageUrl}
              >
                {insertImageState.uploading
                  ? "Uploading..."
                  : "Add Inline Image"}
              </Button>
              <DialogDrawerClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogDrawerClose>
            </DialogDrawerFooter>
          </DialogDrawerContent>
        </DialogDrawer>

        <DialogDrawer
          nested
          open={insertLinkDialogOpen}
          onOpenChange={(open) => {
            if (open) {
              setInsertLinkDialogOpen(true);
              return;
            }

            resetInsertLink();
            setInsertLinkDialogOpen(false);
          }}
        >
          <DialogDrawerContent className="sm:max-w-[425px]">
            <DialogDrawerHeader>
              <DialogDrawerTitle className="flex items-center gap-2">
                <LuLink className="text-muted" />
                {insertLinkState.editing ? "Link Options" : "Insert Link"}
              </DialogDrawerTitle>
              <DialogDrawerDescription className="hidden">
                {insertLinkState.editing ? "Edit link." : "Insert link."}
              </DialogDrawerDescription>
            </DialogDrawerHeader>
            <div className="flex flex-col gap-6 p-6 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="editor-v3-link-text">Text</Label>
                <Input
                  id="editor-v3-link-text"
                  placeholder="Link text"
                  value={insertLinkState.text}
                  onChange={(event) => {
                    setInsertLinkState((prev) => ({
                      ...prev,
                      text: event.target.value,
                    }));
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="editor-v3-link-url">URL</Label>
                <Input
                  id="editor-v3-link-url"
                  placeholder="https://..."
                  value={insertLinkState.url}
                  onChange={(event) => {
                    setInsertLinkState((prev) => ({
                      ...prev,
                      url: event.target.value,
                    }));
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="editor-v3-link-title">Title</Label>
                <Input
                  id="editor-v3-link-title"
                  placeholder="Optional title"
                  value={insertLinkState.title}
                  onChange={(event) => {
                    setInsertLinkState((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            <DialogDrawerFooter className="flex flex-row items-center gap-2">
              {insertLinkState.editing && (
                <Button
                  className="mr-auto text-destructive hover:text-destructive"
                  type="button"
                  variant="ghost"
                  onClick={removeCurrentLink}
                >
                  Remove Link
                </Button>
              )}
              <Button
                disabled={!insertLinkState.url.trim()}
                onClick={insertCurrentLink}
              >
                {insertLinkState.editing ? "Save Link" : "Insert Link"}
              </Button>
              <DialogDrawerClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogDrawerClose>
            </DialogDrawerFooter>
          </DialogDrawerContent>
        </DialogDrawer>
      </div>
    </div>
  );
};
